import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Handshake, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  type AdminCourseRevenueShare,
  useAdminCourseRevenueSharesQuery,
  useAdminSetCourseRevenueShareActiveMutation,
  useAdminUpsertCourseRevenueShareMutation,
  useAdminUsersLazyQuery,
  useGetCoursesQuery,
} from '@/generated/graphql';
import { useToast } from '@/hooks/use-toast';
import { formatCents } from '@/lib/format/currency';

type FormState = {
  courseId: string;
  userId: string;
  rateText: string;
  active: boolean;
  note: string;
};

const EMPTY_FORM: FormState = {
  courseId: '',
  userId: '',
  rateText: '',
  active: true,
  note: '',
};

const STABLE_ERROR_KEYS = ['producer_share_total_exceeds_100'] as const;
const PRODUCER_PAGE_SIZE = 20;

type ErrorOperation = 'save' | 'activate' | 'deactivate' | 'refresh';

const normalizeCourseLocale = (language: string) =>
  language.toLowerCase().startsWith('en') ? 'en' : 'pt';

export const AdminCourseRevenueShares = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminCourseRevenueShare | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [producerSearch, setProducerSearch] = useState('');
  const [activeTarget, setActiveTarget] = useState<AdminCourseRevenueShare | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTitleKey, setErrorTitleKey] = useState(
    'admin.courseRevenueShares.errorTitles.save',
  );
  const [submitting, setSubmitting] = useState(false);
  const [activeOperation, setActiveOperation] = useState<{
    id: string;
    active: boolean;
  } | null>(null);
  const submittingRef = useRef(false);
  const activeOperationRef = useRef(false);

  const { data, loading, error, refetch } = useAdminCourseRevenueSharesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {
    data: coursesData,
    loading: coursesLoading,
    error: coursesError,
    refetch: refetchCourses,
  } = useGetCoursesQuery({
    variables: { locale: normalizeCourseLocale(i18n.language) },
    notifyOnNetworkStatusChange: true,
  });
  const [
    loadProducers,
    { data: producersData, loading: producersLoading, error: producersError },
  ] = useAdminUsersLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [upsertShare] = useAdminUpsertCourseRevenueShareMutation();
  const [setShareActive] = useAdminSetCourseRevenueShareActiveMutation();

  const shares = data?.adminCourseRevenueShares ?? [];
  const courses = coursesData?.courses ?? [];
  const users = editing
    ? [
        {
          id: editing.user_id,
          name: editing.user_name,
          email: editing.user_email,
        },
      ]
    : (producersData?.adminUsers?.data ?? []);
  const parsedRate = Number(form.rateText);
  const validRate =
    form.rateText.trim() !== '' &&
    Number.isInteger(parsedRate) &&
    parsedRate >= 0 &&
    parsedRate <= 100;
  const canSubmit =
    validRate &&
    (editing !== null || (form.courseId.trim() !== '' && form.userId.trim() !== ''));
  const isWorkflowBusy = submitting || activeOperation !== null;

  const producerVariables = () => {
    const search = producerSearch.trim();
    return {
      page: 1,
      pageSize: PRODUCER_PAGE_SIZE,
      ...(search ? { search } : {}),
    };
  };

  useEffect(() => {
    if (!formOpen || editing) return;

    const search = producerSearch.trim();
    const handle = window.setTimeout(
      () => {
        void loadProducers({
          variables: {
            page: 1,
            pageSize: PRODUCER_PAGE_SIZE,
            ...(search ? { search } : {}),
          },
        });
      },
      search ? 300 : 0,
    );

    return () => window.clearTimeout(handle);
  }, [editing, formOpen, loadProducers, producerSearch]);

  const showOperationError = (operation: ErrorOperation, caught?: unknown) => {
    const message = caught instanceof Error ? caught.message : String(caught);
    const stableKey =
      operation === 'save'
        ? STABLE_ERROR_KEYS.find((key) => message.includes(key))
        : undefined;
    const messageKey = stableKey
      ? `admin.courseRevenueShares.errors.${stableKey}`
      : operation === 'save'
        ? 'admin.courseRevenueShares.errors.generic'
        : `admin.courseRevenueShares.errors.${operation}`;

    setErrorTitleKey(`admin.courseRevenueShares.errorTitles.${operation}`);
    setErrorMessage(t(messageKey));
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
    setProducerSearch('');
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setProducerSearch('');
    setFormOpen(true);
  };

  const openEdit = (share: AdminCourseRevenueShare) => {
    setEditing(share);
    setForm({
      courseId: share.course_id,
      userId: share.user_id,
      rateText: String(share.rate_percent),
      active: share.active,
      note: share.note ?? '',
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    const wasEditing = editing !== null;

    try {
      await upsertShare({
        variables: {
          id: editing?.id,
          input: {
            ...(editing
              ? {}
              : {
                  courseId: form.courseId,
                  userId: form.userId,
                }),
            ratePercent: parsedRate,
            active: form.active,
            note: form.note.trim() || null,
          },
        },
      });
    } catch (caught) {
      showOperationError('save', caught);
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    toast({
      title: wasEditing
        ? t('admin.courseRevenueShares.updated')
        : t('admin.courseRevenueShares.created'),
    });

    try {
      await refetch();
    } catch (caught) {
      showOperationError('refresh', caught);
    } finally {
      closeForm();
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleSetActive = async (share: AdminCourseRevenueShare, active: boolean) => {
    if (activeOperationRef.current) return;
    activeOperationRef.current = true;
    setActiveOperation({ id: share.id, active });
    const operation: ErrorOperation = active ? 'activate' : 'deactivate';

    try {
      await setShareActive({ variables: { id: share.id, active } });
    } catch (caught) {
      setActiveTarget(null);
      showOperationError(operation, caught);
      activeOperationRef.current = false;
      setActiveOperation(null);
      return;
    }

    setActiveTarget(null);
    toast({
      title: active
        ? t('admin.courseRevenueShares.activated')
        : t('admin.courseRevenueShares.deactivated'),
    });

    try {
      await refetch();
    } catch (caught) {
      showOperationError('refresh', caught);
    } finally {
      activeOperationRef.current = false;
      setActiveOperation(null);
    }
  };

  return (
    <Layout>
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-[1440px] space-y-4">
          <Card className="overflow-hidden">
            <CardHeader className="border-b bg-muted/20 pb-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1.5">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md border bg-background">
                      <Handshake className="h-4 w-4" />
                    </span>
                    {t('admin.courseRevenueShares.title')}
                  </CardTitle>
                  <p className="max-w-2xl text-sm text-muted-foreground">
                    {t('admin.courseRevenueShares.description')}
                  </p>
                </div>
                <Button disabled={isWorkflowBusy} onClick={openCreate}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('admin.courseRevenueShares.add')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading && !data ? (
                <p className="p-10 text-center text-sm text-muted-foreground">
                  {t('admin.courseRevenueShares.loading')}
                </p>
              ) : error ? (
                <p className="p-10 text-center text-sm text-destructive">
                  {t('admin.courseRevenueShares.loadError')}
                </p>
              ) : shares.length === 0 ? (
                <div className="flex flex-col items-center gap-3 p-12 text-center">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border bg-muted/30">
                    <Handshake className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">{t('admin.courseRevenueShares.empty')}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('admin.courseRevenueShares.emptyDescription')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/10 hover:bg-muted/10">
                        <TableHead>{t('admin.courseRevenueShares.course')}</TableHead>
                        <TableHead>{t('admin.courseRevenueShares.producer')}</TableHead>
                        <TableHead>{t('admin.courseRevenueShares.rate')}</TableHead>
                        <TableHead>{t('admin.courseRevenueShares.active')}</TableHead>
                        <TableHead>{t('admin.courseRevenueShares.note')}</TableHead>
                        <TableHead className="text-right">
                          {t('admin.courseRevenueShares.accrued')}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('admin.courseRevenueShares.pending')}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('admin.courseRevenueShares.available')}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('admin.courseRevenueShares.reversed')}
                        </TableHead>
                        <TableHead className="text-right">
                          {t('admin.courseRevenueShares.actions')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shares.map((share) => (
                        <TableRow key={share.id}>
                          <TableCell className="min-w-48 font-medium">
                            {share.course_title}
                          </TableCell>
                          <TableCell className="min-w-52">
                            <div className="font-medium">{share.user_name || '—'}</div>
                            <div className="text-xs text-muted-foreground">
                              {share.user_email}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-sm font-semibold tabular-nums">
                              {share.rate_percent}%
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={share.active ? 'default' : 'secondary'}>
                              {share.active
                                ? t('admin.courseRevenueShares.active')
                                : t('admin.courseRevenueShares.inactive')}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-56 truncate text-sm text-muted-foreground">
                            {share.note || '—'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm tabular-nums">
                            {formatCents(share.totals.accrued_cents, 'BRL')}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm tabular-nums">
                            {formatCents(share.totals.pending_cents, 'BRL')}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-medium tabular-nums">
                            {formatCents(share.totals.available_cents, 'BRL')}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm tabular-nums text-muted-foreground">
                            {formatCents(share.totals.reversed_cents, 'BRL')}
                          </TableCell>
                          <TableCell>
                            <div className="flex min-w-max justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isWorkflowBusy}
                                onClick={() => openEdit(share)}
                              >
                                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                                {t('admin.courseRevenueShares.edit')}
                              </Button>
                              {share.active ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={isWorkflowBusy}
                                  onClick={() => setActiveTarget(share)}
                                >
                                  <PowerOff className="mr-1.5 h-3.5 w-3.5" />
                                  {t('admin.courseRevenueShares.deactivate')}
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={isWorkflowBusy}
                                  onClick={() => handleSetActive(share, true)}
                                >
                                  <Power className="mr-1.5 h-3.5 w-3.5" />
                                  {t('admin.courseRevenueShares.activate')}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          if (!open && !submitting) closeForm();
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t('admin.courseRevenueShares.editTitle')
                : t('admin.courseRevenueShares.createTitle')}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? t('admin.courseRevenueShares.editDescription')
                : t('admin.courseRevenueShares.createDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-5 py-2">
            <div className="grid gap-2">
              <Label htmlFor="course-revenue-share-course">
                {t('admin.courseRevenueShares.course')}
              </Label>
              <Select
                disabled={
                  editing !== null ||
                  submitting ||
                  coursesLoading ||
                  !!coursesError ||
                  courses.length === 0
                }
                value={form.courseId}
                onValueChange={(courseId) => setForm((current) => ({ ...current, courseId }))}
              >
                <SelectTrigger id="course-revenue-share-course">
                  <SelectValue
                    placeholder={t('admin.courseRevenueShares.selectCourse')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {editing &&
                  !courses.some((course) => course.id === editing.course_id) ? (
                    <SelectItem value={editing.course_id}>
                      {editing.course_title}
                    </SelectItem>
                  ) : null}
                  {courses.map((course) => (
                    <SelectItem key={course.id} value={course.id}>
                      {course.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!editing && coursesLoading ? (
                <p
                  className="text-xs text-muted-foreground"
                  role="status"
                  aria-label={t('admin.courseRevenueShares.coursesLoading')}
                  aria-live="polite"
                >
                  {t('admin.courseRevenueShares.coursesLoading')}
                </p>
              ) : !editing && coursesError ? (
                <div
                  className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
                  role="alert"
                >
                  <span className="text-xs text-destructive">
                    {t('admin.courseRevenueShares.coursesLoadError')}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={submitting}
                    onClick={() => void refetchCourses()}
                  >
                    {t('admin.courseRevenueShares.retryCourses')}
                  </Button>
                </div>
              ) : !editing && courses.length === 0 ? (
                <p
                  className="text-xs text-muted-foreground"
                  role="status"
                  aria-live="polite"
                >
                  {t('admin.courseRevenueShares.noCourses')}
                </p>
              ) : null}
            </div>

            <div className="grid gap-2">
              {!editing ? (
                <>
                  <Label htmlFor="course-revenue-share-user-search">
                    {t('admin.courseRevenueShares.searchProducer')}
                  </Label>
                  <Input
                    id="course-revenue-share-user-search"
                    type="search"
                    value={producerSearch}
                    disabled={submitting}
                    onChange={(event) => {
                      setProducerSearch(event.target.value);
                      setForm((current) => ({ ...current, userId: '' }));
                    }}
                    placeholder={t(
                      'admin.courseRevenueShares.searchProducerPlaceholder',
                    )}
                  />
                  {producersLoading ? (
                    <p
                      className="text-xs text-muted-foreground"
                      role="status"
                      aria-label={t('admin.courseRevenueShares.producersLoading')}
                      aria-live="polite"
                    >
                      {t('admin.courseRevenueShares.producersLoading')}
                    </p>
                  ) : producersError ? (
                    <div
                      className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2"
                      role="alert"
                    >
                      <span className="text-xs text-destructive">
                        {t('admin.courseRevenueShares.producersLoadError')}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={submitting}
                        onClick={() =>
                          void loadProducers({ variables: producerVariables() })
                        }
                      >
                        {t('admin.courseRevenueShares.retryProducers')}
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : null}
              <Label htmlFor="course-revenue-share-user">
                {t('admin.courseRevenueShares.producer')}
              </Label>
              <Select
                disabled={
                  editing !== null ||
                  submitting ||
                  producersLoading ||
                  !!producersError
                }
                value={form.userId}
                onValueChange={(userId) => setForm((current) => ({ ...current, userId }))}
              >
                <SelectTrigger id="course-revenue-share-user">
                  <SelectValue
                    placeholder={t('admin.courseRevenueShares.selectProducer')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name ? `${user.name} — ${user.email}` : user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!editing && !producersLoading && !producersError && users.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t('admin.courseRevenueShares.noProducers')}
                </p>
              ) : null}
              {editing ? (
                <p className="text-xs text-muted-foreground">
                  {t('admin.courseRevenueShares.identityLocked')}
                </p>
              ) : null}
            </div>

            <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <div className="grid gap-2">
                <Label htmlFor="course-revenue-share-rate">
                  {t('admin.courseRevenueShares.rate')}
                </Label>
                <div className="relative">
                  <Input
                    id="course-revenue-share-rate"
                    inputMode="numeric"
                    min={0}
                    max={100}
                    step={1}
                    type="number"
                    value={form.rateText}
                    disabled={submitting}
                    aria-invalid={
                      !validRate && form.rateText !== '' ? 'true' : 'false'
                    }
                    aria-describedby="course-revenue-share-rate-help"
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        rateText: event.target.value,
                      }))
                    }
                    className="pr-9 font-mono tabular-nums"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    %
                  </span>
                </div>
                <p
                  id="course-revenue-share-rate-help"
                  className={
                    !validRate && form.rateText !== ''
                      ? 'text-xs text-destructive'
                      : 'text-xs text-muted-foreground'
                  }
                >
                  {!validRate && form.rateText !== ''
                    ? t('admin.courseRevenueShares.rateValidation')
                    : t('admin.courseRevenueShares.rateHint')}
                </p>
              </div>
              <div className="flex h-10 items-center gap-3 rounded-md border px-3">
                <Switch
                  id="course-revenue-share-active"
                  checked={form.active}
                  disabled={submitting}
                  onCheckedChange={(active) =>
                    setForm((current) => ({ ...current, active }))
                  }
                />
                <Label htmlFor="course-revenue-share-active">
                  {t('admin.courseRevenueShares.active')}
                </Label>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="course-revenue-share-note">
                {t('admin.courseRevenueShares.note')}
              </Label>
              <Textarea
                id="course-revenue-share-note"
                value={form.note}
                disabled={submitting}
                maxLength={500}
                onChange={(event) =>
                  setForm((current) => ({ ...current, note: event.target.value }))
                }
                placeholder={t('admin.courseRevenueShares.notePlaceholder')}
                className="min-h-24 resize-y"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={submitting} onClick={closeForm}>
              {t('admin.courseRevenueShares.cancel')}
            </Button>
            <Button disabled={!canSubmit || submitting} onClick={handleSave}>
              {editing
                ? t('admin.courseRevenueShares.save')
                : t('admin.courseRevenueShares.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={activeTarget !== null}
        onOpenChange={(open) => {
          if (!open && activeOperation === null) setActiveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.courseRevenueShares.deactivateTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.courseRevenueShares.deactivateDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={activeOperation !== null}
              onClick={() => setActiveTarget(null)}
            >
              {t('admin.courseRevenueShares.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={activeOperation !== null}
              onClick={() => {
                if (activeTarget) handleSetActive(activeTarget, false);
              }}
            >
              {t('admin.courseRevenueShares.confirmDeactivate')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={errorMessage !== null}
        onOpenChange={(open) => {
          if (!open) setErrorMessage(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(errorTitleKey)}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorMessage(null)}>
              {t('admin.courseRevenueShares.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminCourseRevenueShares;
